import { AddressSearchWidget, AddressSearchWidgetProps } from '@melio/ap-widgets';
import { Form, useMelioForm } from '@melio/penny';
import { useAnalytics } from '@melio/platform-analytics';
import { Address } from '@melio/platform-api';
import { useMelioIntl } from '@melio/platform-i18n';
import { useEffect, useMemo } from 'react';

import { getVendorMissingDetailsList } from '../../../BatchPayments/screens/PaymentIntentsTable/PaymentIntentsTable.screen.analytics-utils';
import { useAddressFormStateOptions } from './hooks/useAddressFormStateOptions.hook';
import { useSchema } from './hooks/useSchema.hook';
import { VendorDetailsFormFields, VendorDetailsFormProps } from './types';
import { VendorMccFields } from './VendorMccFields';

export const VendorDetailsForm = ({
  onSubmit,
  onSubmissionStateChange,
  isSaving,
  mccIndustryList,
  requiredFormFields,
  cardNetwork,
}: VendorDetailsFormProps) => {
  const { track } = useAnalytics();
  const { formatMessage } = useMelioIntl();
  const { options: statesOptions } = useAddressFormStateOptions();
  const requiredFields = useMemo(
    () => ({
      mcc: requiredFormFields.includes('mcc'),
      address: requiredFormFields.includes('address'),
      email: requiredFormFields.includes('email'),
      phone: requiredFormFields.includes('phone'),
    }),
    [requiredFormFields]
  );
  const mccGroupsSupported = useMemo(() => mccIndustryList.some((x) => Boolean(x.category)), [mccIndustryList]);

  const form = useMelioForm<VendorDetailsFormFields>({
    onSubmit,
    schema: useSchema(requiredFields, mccGroupsSupported),
    defaultValues: {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore - if we pass '' search works properly only after 2nd search input
      line1: null,
    },
    isSaving,
    onSubmissionStateChange,
  });

  useEffect(() => {
    if (Object.values(form.formState.errors).length) {
      track('PaymentMethodOptions', 'Status', {
        PageName: 'add-vendor-details',
        DetailsToFill: getVendorMissingDetailsList(requiredFormFields),
        CardNetwork: cardNetwork,
        Intent: 'add-vendor-business-details',
        ErrorType: 'invalid-values',
        Status: 'failure',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.formState.errors]);

  const handleAddressChange: AddressSearchWidgetProps['onChange'] = (event) => {
    const address = event.target.value as unknown as Address;
    const isFormValueCleared = event.target.value === null;

    if (isFormValueCleared && form.getValues('line1')) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore - if we pass '' search works properly only after 2nd search input
      setValue('line1', null);
    }

    const setFormField = (field: keyof Omit<Address, 'countryCode' | 'line2' | ''>) => {
      if (address?.[field]) {
        form.setValue(field, address[field], {
          shouldValidate: true,
        });
      }
    };

    setFormField('line1');
    setFormField('state');
    setFormField('city');
    setFormField('postalCode');
  };

  return (
    <Form data-component="VendorDetailsForm" columns={2} size="small" {...form.formProps}>
      {requiredFields.mcc && (
        <VendorMccFields form={form} mccIndustryList={mccIndustryList} mccGroupsSupported={mccGroupsSupported} />
      )}
      {requiredFields.email && (
        <Form.TextField
          size="small"
          colSpan={2}
          {...form.registerField('email')}
          labelProps={{ label: formatMessage('activities.fundingSources.vendorDetailsForm.fields.email.label') }}
          placeholder={formatMessage('activities.fundingSources.vendorDetailsForm.fields.email.placeholder')}
          data-testid="vendor-details-email-input"
        />
      )}
      {requiredFields.phone && (
        <Form.PhoneField
          size="small"
          colSpan={2}
          {...form.registerField('phone')}
          labelProps={{ label: formatMessage('activities.fundingSources.vendorDetailsForm.fields.phone.label') }}
          placeholder={formatMessage('activities.fundingSources.vendorDetailsForm.fields.phone.placeholder')}
          data-testid="vendor-details-phone-input"
        />
      )}
      {requiredFields.address && (
        <>
          <AddressSearchWidget
            size="small"
            {...form.registerField('line1')}
            colSpan={2}
            onChange={handleAddressChange}
            formatSelectedValue={(option) =>
              (option.value as unknown as Address).line1 || (option.value as unknown as string)
            }
            labelProps={{
              label: formatMessage('activities.fundingSources.vendorDetailsForm.fields.line1.label'),
            }}
            placeholder={formatMessage('activities.fundingSources.vendorDetailsForm.fields.line1.placeholder')}
          />
          <Form.TextField
            size="small"
            colSpan={2}
            labelProps={{
              label: formatMessage('activities.fundingSources.vendorDetailsForm.fields.city.label'),
            }}
            {...form.registerField('city')}
            data-testid="vendor-details-address-city-input"
          />
          <Form.DeprecatedSelect
            size="small"
            colSpan={1}
            labelProps={{
              label: formatMessage('activities.fundingSources.vendorDetailsForm.fields.state.label'),
            }}
            placeholder={formatMessage('activities.fundingSources.vendorDetailsForm.fields.state.placeholder')}
            {...form.registerField('state')}
            options={statesOptions}
            data-testid="vendor-details-address-state-input"
          />
          <Form.TextField
            size="small"
            colSpan={1}
            labelProps={{
              label: formatMessage('activities.fundingSources.vendorDetailsForm.fields.postalCode.label'),
            }}
            {...form.registerField('postalCode')}
            data-testid="vendor-details-address-postalCode-input"
          />
        </>
      )}
    </Form>
  );
};
